import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import './Login.css';

function isPasswordStrong(password) {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password) &&
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  );
}

export default function Login() {
  const { isAuthenticated, loginCandidate, setCandidatePassword, loginSSO, error, isLoading, setUserDirect } = useAuth();
  const [mode, setMode] = useState('select');

  // Candidate form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  // Flow visualization state (dev only — hidden in production)
  const [showFlow, setShowFlow] = useState(false);
  const [flowSteps, setFlowSteps] = useState([]);
  const [flowComplete, setFlowComplete] = useState(false);
  const isDevMode = process.env.NODE_ENV === 'development';

  // New password challenge state
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [challengeSession, setChallengeSession] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Instructions modal state
  const [showInstructions, setShowInstructions] = useState(false);

  // Clear stale auth data on Login page load (prevents issues when switching candidates)
  React.useEffect(() => {
    sessionStorage.removeItem('commscoach_user');
    sessionStorage.removeItem('commscoach_token');
    localStorage.removeItem('commscoach_token');
    localStorage.removeItem('token');
    // Clear session_accepted flags from previous exams
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('session_accepted_')) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  if (isLoading) {
    return (
      <div className="login-container">
        <div className="login-loading">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (isAuthenticated && !showFlow) return <Navigate to="/" replace />;

  const displayError = localError || error;

  // Helper to add flow steps with delay
  const addFlowStep = (step) => {
    return new Promise((resolve) => {
      setFlowSteps(prev => [...prev, step]);
      setTimeout(resolve, 800);
    });
  };

  // Candidate login handler with flow visualization
  const handleCandidateLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSubmitting(true);
    if (isDevMode) {
      setShowFlow(true);
      setFlowSteps([]);
      setFlowComplete(false);
    }

    try {
      // Step 1: Show initial step
      await addFlowStep({ id: 1, label: 'Candidate enters Email + Password', status: 'done', icon: '👤' });

      // Step 2: Check Redis
      await addFlowStep({ id: 2, label: 'Checking Redis for valid JWT...', status: 'loading', icon: '🔍' });

      const { checkCandidateSession, candidateLogin: apiCandidateLogin } = await import('../auth/authApi');
      const sessionCheck = await checkCandidateSession(email);

      if (sessionCheck.blocked) {
        setFlowSteps(prev => prev.map(s => s.id === 2 ? { ...s, status: 'done', label: 'Token found in Redis — checking access_status...' } : s));
        await addFlowStep({ id: 3, label: `🚫 access_status: "deactive" — Account blocked`, status: 'error', icon: '🔒' });
        await addFlowStep({ id: 4, label: sessionCheck.reason || 'Your account has been deactivated.', status: 'error', icon: '❌' });
        setLocalError(sessionCheck.reason || 'Account deactivated');
        setSubmitting(false);
        return;
      }

      if (sessionCheck.valid && sessionCheck.user) {
        // Redis HIT path
        setFlowSteps(prev => prev.map(s => s.id === 2 ? { ...s, status: 'done', label: '✅ Valid JWT found in Redis! (Session cached)' } : s));

        // Show access_status
        const accessStatus = sessionCheck.access_status || 'active';
        await addFlowStep({ id: 3, label: `Access Status: "${accessStatus}" — User: ${sessionCheck.user.email}`, status: 'done', icon: '🟢' });

        if (sessionCheck.has_active_exam) {
          await addFlowStep({ id: 4, label: 'Active session found → Restoring session...', status: 'done', icon: '📋' });
        } else {
          await addFlowStep({ id: 4, label: 'No active session → Redirecting to Dashboard', status: 'done', icon: '📋' });
        }

        await addFlowStep({ id: 5, label: '✅ Skipped Cognito! Session restored from Redis → Redirecting...', status: 'done', icon: '🎯' });

        // Set user directly without triggering another API call
        setUserDirect({
          id: sessionCheck.user.id,
          username: sessionCheck.user.email.split('@')[0],
          email: sessionCheck.user.email,
          full_name: sessionCheck.user.full_name,
          role: sessionCheck.user.role,
        });
        setFlowComplete(true);
        setTimeout(() => { setShowFlow(false); }, 2500);
        return;
      }

      // Redis MISS path
      setFlowSteps(prev => prev.map(s => s.id === 2 ? { ...s, status: 'done', label: '⚠️ No valid token in Redis (expired or missing)' } : s));

      // Step 3: Cognito auth - call API directly to get the raw result
      await addFlowStep({ id: 3, label: 'Sending credentials to AWS Cognito...', status: 'loading', icon: '🔐' });

      const cognitoResult = await apiCandidateLogin(email, password);

      if (cognitoResult.challenge === 'NEW_PASSWORD_REQUIRED') {
        // NEW PASSWORD path - user has never logged in before
        setFlowSteps(prev => prev.map(s => s.id === 3 ? { ...s, status: 'done', label: '⚠️ Cognito requires new password (first-time login)' } : s));
        await addFlowStep({ id: 4, label: 'Redirecting to Set New Password form...', status: 'done', icon: '🔑' });

        setChallengeSession(cognitoResult.session || '');
        setTimeout(() => {
          setShowNewPassword(true);
          setShowFlow(false);
        }, 1500);
        return;
      }

      if (cognitoResult.success && cognitoResult.user && cognitoResult.token) {
        // Success path - call AuthProvider to set state properly
        setFlowSteps(prev => prev.map(s => s.id === 3 ? { ...s, status: 'done', label: '✅ Cognito authentication successful!' } : s));
        await addFlowStep({ id: 4, label: `Access Status: "${cognitoResult.user.role}" — User: ${cognitoResult.user.email}`, status: 'done', icon: '🟢' });
        await addFlowStep({ id: 5, label: 'JWT stored in Redis against email (TTL: 1 hour)', status: 'done', icon: '💾' });
        await addFlowStep({ id: 6, label: '✅ Next login within 1hr will use Redis (skip Cognito)', status: 'done', icon: '⚡' });
        await addFlowStep({ id: 7, label: '✅ Redirecting to Candidate Dashboard...', status: 'done', icon: '🎯' });

        // Set user directly — token is already stored in Redis by the backend
        setUserDirect({
          id: cognitoResult.user.id,
          username: cognitoResult.user.email.split('@')[0],
          email: cognitoResult.user.email,
          full_name: cognitoResult.user.full_name,
          role: cognitoResult.user.role,
        });
        setFlowComplete(true);
        setTimeout(() => { setShowFlow(false); }, 2500);
        return;
      }

      // Auth failed
      const errMsg = cognitoResult.error || 'Invalid credentials';
      setFlowSteps(prev => prev.map(s => s.id === 3 ? { ...s, status: 'error', label: `❌ Cognito authentication failed` } : s));
      await addFlowStep({ id: 4, label: `Authentication Failed: ${errMsg}`, status: 'error', icon: '❌' });
      setLocalError(errMsg);

    } catch (err) {
      // Error path
      const errMsg = err instanceof Error ? err.message : 'Login failed';
      setFlowSteps(prev => {
        const updated = prev.map(s => s.status === 'loading' ? { ...s, status: 'error', label: `❌ ${s.label.replace('...', '')} — Failed` } : s);
        return [...updated, { id: prev.length + 1, label: `Error: ${errMsg}`, status: 'error', icon: '❌' }];
      });
      setLocalError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // New password handler
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (!isPasswordStrong(newPassword)) {
      setLocalError('Password does not meet requirements');
      return;
    }

    setSubmitting(true);
    try {
      await setCandidatePassword(email, newPassword, challengeSession);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  // Login method selection
  if (mode === 'select') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="logo-section">
            <img src={`${process.env.PUBLIC_URL}/quantum-forge-logo.svg`} alt="Quantum Forge" className="login-logo" />
            <h1>Quantum Forge</h1>
            <p>AI based comms coach portal</p>
          </div>

          <div className="login-options">
            <button type="button" className="btn btn-sso" onClick={() => { setMode('sso'); loginSSO(); }}>
              <svg className="sso-icon" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="9.5" height="9.5" fill="#F25022"/>
                <rect x="11.5" width="9.5" height="9.5" fill="#7FBA00"/>
                <rect y="11.5" width="9.5" height="9.5" fill="#00A4EF"/>
                <rect x="11.5" y="11.5" width="9.5" height="9.5" fill="#FFB900"/>
              </svg>
              SSO Admin Login
            </button>

            <button type="button" className="btn btn-candidate" onClick={() => setMode('candidate')}>
              <svg className="candidate-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              Candidate Login
            </button>

            <button type="button" className="btn btn-instructions" onClick={() => setShowInstructions(true)}>
              <svg className="instructions-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
              How to Give Assessment
            </button>

            {/* Dev-only: simulate SSO admin login for local testing */}
            {process.env.NODE_ENV === 'development' && (
            <button type="button" className="btn btn-dev-admin" onClick={async () => {
              try {
                const apiBase = process.env.REACT_APP_API_URL || '/api';
                const response = await fetch(`${apiBase}/auth/dev-admin-login`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                });
                if (response.ok) {
                  const data = await response.json();
                  const { setAccessToken } = await import('../auth/tokenService');
                  setAccessToken(data.access_token);
                  setUserDirect(data.user);
                }
              } catch (err) {
                setLocalError('Dev login failed: ' + err.message);
              }
            }}>
              🛠️ Dev: Login as Admin (local testing)
            </button>
            )}
          </div>
        </div>

        {/* Instructions Modal */}
        {showInstructions && (
          <div className="instructions-overlay" onClick={() => setShowInstructions(false)}>
            <div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
              <div className="instructions-header">
                <h2>How to Give Assessment</h2>
                <button className="instructions-close" onClick={() => setShowInstructions(false)} aria-label="Close">✕</button>
              </div>

              <div className="instructions-body">
                <section className="instructions-section">
                  <h3>🧹 Before You Start</h3>
                  <ul className="instructions-list tips-list">
                    <li>Clear your browser cache and close all other tabs before starting</li>
                    <li>Use a stable internet connection and a laptop/desktop (not mobile)</li>
                    <li>Ensure you have at least 45 minutes of uninterrupted time</li>
                  </ul>
                </section>

                <section className="instructions-section">
                  <h3>📋 Overview</h3>
                  <p>You will handle real customer conversations. Each session presents a scenario where you respond to a customer as you would in a real call. You have 3 sessions, each with a different customer scenario.</p>
                </section>

                <section className="instructions-section">
                  <h3>⏱️ Time &amp; Sessions</h3>
                  <ul className="instructions-list warning-list">
                    <li>You have a total of <strong>40 minutes</strong> shared across all 3 sessions</li>
                    <li>The timer starts when you click "Start" on your first session</li>
                    <li><strong>Attending all 3 sessions is mandatory</strong></li>
                    <li><strong>You must pass all 3 sessions</strong> to receive an overall Pass</li>
                    <li>Skipping any session will result in a Fail</li>
                  </ul>
                </section>

                <section className="instructions-section">
                  <h3>🔄 Starting Next Sessions</h3>
                  <ul className="instructions-list tips-list">
                    <li>Session 2 and 3 will appear as pop-ups during your active session — click to accept</li>
                    <li>If you finish a session before the pop-up appears, go back to the <strong>Assessment Portal (Hub)</strong> — the next session will show "Ready to start" once available</li>
                  </ul>
                </section>

                <section className="instructions-section">
                  <h3>🔌 If You Get Disconnected</h3>
                  <ul className="instructions-list warning-list">
                    <li>If your internet drops, the system pauses your session</li>
                    <li>If you reconnect <strong>within 10 minutes</strong>, your exam resumes from where you left off</li>
                    <li>After 10 minutes, the session is <strong>auto-submitted</strong> with whatever answers you've given</li>
                  </ul>
                </section>

                <section className="instructions-section">
                  <h3>🔄 If You Close the Tab by Mistake</h3>
                  <ul className="instructions-list tips-list">
                    <li>Don't panic — go back to the <strong>Assessment Portal (Hub)</strong></li>
                    <li>Click on your in-progress session to resume</li>
                    <li>Your conversation history and progress are saved</li>
                  </ul>
                </section>

                <section className="instructions-section">
                  <h3>✅ Do's</h3>
                  <ul className="instructions-list do-list">
                    <li>Read the scenario carefully before clicking Start</li>
                    <li>Respond naturally as if you are speaking to a real customer</li>
                    <li>Be polite, professional, and empathetic in your responses</li>
                    <li>Use proper grammar and spelling in your answers</li>
                    <li>Take your time to read each customer message before replying</li>
                    <li>Submit your session when you are done or when all interactions are complete</li>
                  </ul>
                </section>

                <section className="instructions-section">
                  <h3>❌ Don'ts</h3>
                  <ul className="instructions-list dont-list">
                    <li>Do not copy-paste answers — copy and paste is disabled</li>
                    <li>Do not close the session tab during the assessment</li>
                    <li>Do not use any external tools, translators, or assistants</li>
                    <li>Do not share your login credentials with anyone</li>
                    <li>Do not leave the assessment idle for long periods</li>
                    <li>Do not refresh the page unnecessarily during the session</li>
                  </ul>
                </section>

                <section className="instructions-section">
                  <h3>⚠️ Important Notes</h3>
                  <ul className="instructions-list warning-list">
                    <li>Each session evaluates you on 6 communication parameters including grammar and spelling</li>
                    <li>Once a session is submitted, you cannot go back to modify your answers</li>
                    <li>Write clearly and completely — your responses will be reviewed by AI</li>
                  </ul>
                </section>

                <section className="instructions-section">
                  <h3>💡 Tips</h3>
                  <ul className="instructions-list tips-list">
                    <li>Greet the customer warmly at the start of each session</li>
                    <li>Address all parts of the customer's query in your response</li>
                    <li>Keep your responses concise but complete</li>
                    <li>Use a professional tone throughout the conversation</li>
                    <li>Proofread your response before sending</li>
                  </ul>
                </section>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Candidate login form
  if (mode === 'candidate') {
    // New password form (after challenge)
    if (showNewPassword) {
      return (
        <div className="login-container">
          <div className="login-card">
            <div className="logo-section">
              <img src={`${process.env.PUBLIC_URL}/bt-logo.svg`} alt="BT Group" className="login-logo" />
              <h1>Set New Password</h1>
              <p>Your temporary password has expired. Please set a new one.</p>
            </div>

            {displayError && (
              <div className="error-message">
                <span className="error-icon">⚠</span>
                {displayError}
              </div>
            )}

            <form onSubmit={handleSetPassword} className="login-form">
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="password-requirements">
                <p>Password must contain:</p>
                <ul>
                  <li>At least 8 characters</li>
                  <li>Uppercase and lowercase letters</li>
                  <li>At least one number</li>
                  <li>At least one special character</li>
                </ul>
              </div>

              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Setting password...' : 'Set Password & Continue'}
              </button>
            </form>

            <button className="back-link" onClick={() => { setShowNewPassword(false); setMode('select'); }}>
              ← Back to login options
            </button>
          </div>
        </div>
      );
    }

    // Standard candidate login
    return (
      <div className="login-container">
        <div className={showFlow ? "login-card login-card-wide" : "login-card"}>
          <div className="logo-section">
            <img src={`${process.env.PUBLIC_URL}/bt-logo.svg`} alt="BT Group" className="login-logo" />
            <h1>Candidate Login</h1>
            <p>Sign in with your email and password</p>
          </div>

          {displayError && !showFlow && (
            <div className="error-message">
              <span className="error-icon">⚠</span>
              {displayError}
            </div>
          )}

          {!showFlow && (
            <form onSubmit={handleCandidateLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  autoComplete="username"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={submitting || !email || !password}>
                {submitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          {showFlow && (
            <div className="flow-panel">
              <h3 className="flow-title">🔄 Authentication Flow</h3>
              <div className="flow-steps">
                {flowSteps.map((step) => (
                  <div key={step.id} className={`flow-step flow-step-${step.status}`}>
                    <span className="flow-step-icon">{step.icon}</span>
                    <span className="flow-step-label">{step.label}</span>
                    {step.status === 'loading' && <span className="flow-step-spinner" />}
                  </div>
                ))}
              </div>
              {flowComplete && (
                <div className="flow-success">
                  <p>✅ All steps complete! Redirecting...</p>
                </div>
              )}
              {displayError && (
                <div className="error-message" style={{marginTop: '16px'}}>
                  <span className="error-icon">⚠</span>
                  {displayError}
                </div>
              )}
              {!submitting && !flowComplete && (
                <button className="back-link" onClick={() => { setShowFlow(false); setFlowSteps([]); setLocalError(''); }}>
                  ← Try again
                </button>
              )}
            </div>
          )}

          {!showFlow && (
            <button className="back-link" onClick={() => { setMode('select'); setLocalError(''); }}>
              ← Back to login options
            </button>
          )}
        </div>
      </div>
    );
  }

  // SSO redirect in progress
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="logo-section">
          <img src={`${process.env.PUBLIC_URL}/bt-logo.svg`} alt="BT Group" className="login-logo" />
          <h1>CommsCoach</h1>
          <p>Redirecting to SSO...</p>
        </div>
        <div className="login-loading">
          <div className="spinner" />
        </div>
      </div>
    </div>
  );
}
